import {type ReactNode} from 'react';

import {CustomFieldList} from 'shells/row/custom-field-row/custom-field-list';

import type {
    GetWidgetCustomFields_widgetCustomFields as WidgetCustomFields,
    WidgetFragment as Widget,
} from 'nutshell-graphql-types';

import type {useCustomFieldHandlers} from '../../../hooks/use-custom-field-handlers';
import {useGetClickToCallProps} from '../../../hooks/use-get-click-to-call-props';

import {CollapsibleCardWidget} from '../../../../core/card/collapsible-card-widget';
import {getWidgetEmptyStateProps} from '../../../../core/card/helpers';
import type {CardWidgetKey} from '../../../../core/card/types';

interface Props {
    entityId: string;
    widget: Widget;
    handlers: ReturnType<typeof useCustomFieldHandlers>;
    emptyStateKey: CardWidgetKey;
    customFields: WidgetCustomFields | null | undefined;
    isFirstLoad: boolean;
    // Used to add injected non-attribute powered rows to this widget
    extraRows?: ReactNode[];
}

export function CustomFieldsWidgetShell(props: Props) {
    const {entityId, handlers, emptyStateKey, extraRows = []} = props;

    const clickToCallProps = useGetClickToCallProps(entityId);

    const fieldCount =
        props.customFields && props.customFields.edges ? props.customFields.edges.length : 0;
    const count = fieldCount + extraRows.length;

    return (
        <CollapsibleCardWidget
            widget={props.widget}
            isLoading={props.isFirstLoad}
            emptyStateProps={getWidgetEmptyStateProps(emptyStateKey, count === 0)}
            count={count}
        >
            {extraRows}
            {props.customFields && (
                <CustomFieldList
                    customFields={props.customFields.edges}
                    handleTextFieldUpdate={handlers.handleTextFieldUpdate}
                    handleEnumUpdate={handlers.handleEnumUpdate}
                    handleCurrencyUpdate={handlers.handleCurrencyUpdate}
                    handleAddressUpdate={handlers.handleAddressUpdate}
                    handlePhoneUpdate={handlers.handlePhoneUpdate}
                    clickToCallProps={clickToCallProps}
                    fieldErrors={handlers.fieldErrors}
                    setErrorForField={handlers.setErrorForField}
                    isWrapping={props.widget.location === 'WORKSPACE'}
                />
            )}
        </CollapsibleCardWidget>
    );
}
